import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface City {
	label: string;
	value: string;
	_id: string;
}

interface CityContextType {
	cities: City[];
	currentCity: City | null;
	setCurrentCity: (city: City | null) => void;
	setCities: (cities: City[]) => void;
	isSuperAdmin: boolean;
	canSelectCity: boolean;
	loading: boolean;
	userAssignedCity: City | null;
	refreshCityData: () => void;
}

const CityContext = createContext<CityContextType | undefined>(undefined);

export const useCity = () => {
	const context = useContext(CityContext);
	if (!context) {
		throw new Error('useCity must be used within a CityProvider');
	}
	return context;
};

interface CityProviderProps {
	children: ReactNode;
}

export const CityProvider: React.FC<CityProviderProps> = ({ children }) => {
	const [cities, setCities] = useState<City[]>([]);
	const [currentCity, setCurrentCity] = useState<City | null>(null);
	const [userAssignedCity, setUserAssignedCity] = useState<City | null>(null);
	const [isSuperAdmin, setIsSuperAdmin] = useState(false);
	const [loading, setLoading] = useState(true);

	const initializeCityData = () => {
		try {
			// Get data from localStorage
			const allCitiesStr = localStorage.getItem('allCities');
			const loggedInCityStr = localStorage.getItem('loggedInCity');
			const userRole = localStorage.getItem('userRole') || '';
			const authDataStr = localStorage.getItem('authData');

			// Parse cities
			let allCities: City[] = [];
			if (allCitiesStr) {
				try {
					allCities = JSON.parse(allCitiesStr);
				} catch (e) {
					console.error('Error parsing allCities:', e);
				}
			}

			// If no cities from allCities, try from authData
			if (allCities.length === 0 && authDataStr) {
				try {
					const authData = JSON.parse(authDataStr);
					if (authData.allCities) {
						allCities = authData.allCities;
					}
				} catch (e) {
					console.error('Error parsing authData:', e);
				}
			}

			setCities(allCities);

			// Check if user is Super Admin
			const isSuperAdminUser = userRole.toLowerCase().includes('super admin');
			setIsSuperAdmin(isSuperAdminUser);

			// Find the assigned city
			let assignedCity: City | null = null;

			if (loggedInCityStr && allCities.length > 0) {
				// Case-insensitive match for city
				const cityValue = loggedInCityStr.trim();
				assignedCity =
					allCities.find(
						(city: City) => city.value.toLowerCase() === cityValue.toLowerCase(),
					) || null;
			}

			setUserAssignedCity(assignedCity);

			// Set current city based on user role
			if (isSuperAdminUser) {
				// For Super Admin: Check localStorage for saved city or use first city
				const savedCity = localStorage.getItem('selectedCity');
				if (savedCity) {
					try {
						const parsedCity = JSON.parse(savedCity);
						const cityExists = allCities.some((c: City) => c._id === parsedCity._id);
						if (cityExists) {
							setCurrentCity(parsedCity);
						} else {
							setCurrentCity(allCities[0] || null);
						}
					} catch {
						setCurrentCity(allCities[0] || null);
					}
				} else {
					setCurrentCity(allCities[0] || null);
				}
			} else {
				// For regular users: Always use their assigned city
				if (assignedCity) {
					setCurrentCity(assignedCity);
				} else if (allCities.length > 0) {
					// If no assigned city found, try to find by loggedInCity from authData
					if (authDataStr) {
						try {
							const authData = JSON.parse(authDataStr);
							const cityFromAuth = authData.loggedInCity;
							if (cityFromAuth) {
								const found = allCities.find(
									(c: City) =>
										c.value.toLowerCase() === cityFromAuth.toLowerCase(),
								);
								if (found) {
									setCurrentCity(found);
									setUserAssignedCity(found);
									return;
								}
							}
						} catch (e) {}
					}
					setCurrentCity(allCities[0] || null);
				}
			}
		} catch (error) {
			console.error('Error initializing city:', error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		initializeCityData();

		// Listen for storage changes
		const handleStorageChange = (e: StorageEvent) => {
			if (
				e.key === 'allCities' ||
				e.key === 'loggedInCity' ||
				e.key === 'userRole' ||
				e.key === 'authData'
			) {
				initializeCityData();
			}
		};

		window.addEventListener('storage', handleStorageChange);

		// Custom event for auth data changes
		const handleAuthChange = () => {
			initializeCityData();
		};
		window.addEventListener('authDataChanged', handleAuthChange);

		return () => {
			window.removeEventListener('storage', handleStorageChange);
			window.removeEventListener('authDataChanged', handleAuthChange);
		};
	}, []);

	// Save selected city to localStorage only for Super Admin
	useEffect(() => {
		if (isSuperAdmin && currentCity) {
			localStorage.setItem('selectedCity', JSON.stringify(currentCity));
		}
	}, [currentCity, isSuperAdmin]);

	const value = {
		cities,
		currentCity,
		setCurrentCity,
		setCities,
		isSuperAdmin,
		canSelectCity: isSuperAdmin,
		loading,
		userAssignedCity,
		refreshCityData: initializeCityData,
	};

	return <CityContext.Provider value={value}>{children}</CityContext.Provider>;
};
